"use client";

import React, { useState, CSSProperties, useEffect, useMemo } from "react";
import { theme } from "@/styles/theme";
import { HumanCaptchaGame } from "@/components/HumanCaptchaGame";

type FieldKey = "name" | "email" | "message";
type Errors = Partial<Record<FieldKey, string>>;
type FocusState = Record<FieldKey, boolean>;

type Stage = "form" | "captcha" | "sending" | "sent";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setIsMobile(window.innerWidth <= breakpoint);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [breakpoint]);
  return isMobile;
}

export function ContactPanel() {
  const isMobile = useIsMobile();
  const orientation = isMobile ? "vertical" : "horizontal";

  const [stage, setStage] = useState<Stage>("form");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const [errors, setErrors] = useState<Errors>({});
  const [focus, setFocus] = useState<FocusState>({
    name: false,
    email: false,
    message: false,
  });

  const [showRequiredHint, setShowRequiredHint] = useState(false);

  // send status
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">(
    "idle"
  );
  const [serverError, setServerError] = useState<string | null>(null);

  // captcha micro-state
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [captchaSolved, setCaptchaSolved] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = (): boolean => {
    const next: Errors = {};
    if (!name.trim()) next.name = "Required";
    if (!email.trim()) next.email = "Required";
    else if (!emailRegex.test(email.trim())) next.email = "Invalid";
    if (!message.trim()) next.message = "Required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildInputStyle = (field: FieldKey): CSSProperties => {
    const hasError = !!errors[field];
    const isFocused = focus[field];

    let border: string | undefined;
    if (hasError) border = "1px solid #FF0031";
    else if (isFocused) border = `1px solid ${theme.colors.Green}`;

    return {
      display: "flex",
      height: 30,
      padding: "4px 8px",
      alignItems: "center",
      gap: 8,
      alignSelf: "stretch",
      width: "100%",
      background: theme.colors.DarkGray,
      color: theme.colors.White,
      boxShadow:
        "-1px -1px 0 0 #FFF inset, 1px 1px 0 0 #808080 inset, -2px -2px 0 0 #C1C1C1 inset, 2px 2px 0 0 #000 inset",
      border: border ?? "none",
      outline: "none",
      fontFamily: theme.fonts.body,
      fontSize: 14,
      boxSizing: "border-box",
    };
  };

  const labelStyle: CSSProperties = {
    color: theme.colors.White,
    fontFamily: theme.fonts.body,
    fontSize: 16,
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    letterSpacing: 0,
    marginBottom: 6,
  };

  // Button visuals (same logic you already have)
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const isSending = status === "sending" || stage === "sending";
  const isVerifying = stage === "captcha";

  let border = `1px solid ${theme.colors.White}`;
  let background = theme.colors.Dark;
  let color = theme.colors.White;
  let boxShadow =
    "-3px -3px 0 0 rgba(127, 127, 127, 0.50) inset, 2px 2px 0 0 " +
    theme.colors.LightGray +
    " inset";
  let cursor: CSSProperties["cursor"] = "pointer";

  if (isSending || isVerifying) {
    cursor = "default";
    boxShadow =
      "-3px -3px 0 0 rgba(100, 100, 100, 0.50) inset, 2px 2px 0 0 rgba(60, 60, 60, 0.80) inset";
  } else if (pressed) {
    background = "#363636";
    boxShadow =
      "-3px -3px 0 0 rgba(150, 150, 150, 0.50) inset, 2px 2px 0 0 rgba(0, 0, 0, 0.50) inset";
  } else if (hovered) {
    border = `2px solid ${theme.colors.Green}`;
    color = theme.colors.Green;
    boxShadow = "none";
  }

  const sendLabel = useMemo(() => {
    if (stage === "captcha") return "Human verification...";
    if (stage === "sending") return "Sending...";
    if (status === "sending") return "Sending...";
    return "Send";
  }, [stage, status]);

  const beginCaptcha = async () => {
    setCaptchaError(null);
    setCaptchaSolved(false);

    // Create server-side challenge cookie (prevents trivial bypass)
    try {
      const res = await fetch("/api/captcha/challenge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Captcha challenge failed");
      }

      // FORM fades out; beveled overlay fades in (captcha)
      setStage("captcha");
    } catch (err) {
      console.error("captcha/challenge error:", err);
      setCaptchaError("Could not start verification. Please try again.");
      setStage("form");
    }
  };

  const finalizeCaptchaAndSend = async () => {
    setCaptchaError(null);

    try {
      // Verify server-side (sets a short-lived HttpOnly cookie)
      const v = await fetch("/api/captcha/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
      });

      const vdata = await v.json().catch(() => null);
      if (!v.ok || !vdata?.ok) {
        throw new Error(vdata?.error || "Captcha verification failed");
      }

      // Now proceed to sending — still inside beveled overlay
      setStage("sending");
      setStatus("sending");

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Unknown error");
      }

      setStatus("success");
      setStage("sent");

      // Clear input fields after success (optional)
      setName("");
      setEmail("");
      setMessage("");
      setErrors({});
    } catch (err) {
      console.error("captcha/send error:", err);
      setStatus("error");
      setServerError("Something went wrong sending your message. Please try again.");
      // Force re-verify on retry
      setCaptchaSolved(false);
      setStage("form");
    }
  };

  const handleSubmit = async () => {
    setServerError(null);
    setCaptchaError(null);

    if (stage !== "form") return;

    const isValid = validate();
    if (!isValid) {
      if (!name.trim() && !email.trim() && !message.trim()) {
        setShowRequiredHint(true);
      }
      return;
    }

    setShowRequiredHint(false);
    setStatus("idle");

    // Change button label immediately (still visible during fade)
    await beginCaptcha();
  };

  // Fade out the "*Required field" once user completes any field
  useEffect(() => {
    if (!showRequiredHint) return;
    if (name.trim() || email.trim() || message.trim()) setShowRequiredHint(false);
  }, [name, email, message, showRequiredHint]);

  const fadeMs = 220;

  // Form layer: show ONLY on "form" (never comes back during captcha/sending/sent flow)
  const layerStyle = (active: boolean): CSSProperties => ({
    position: "absolute",
    inset: 0,
    opacity: active ? 1 : 0,
    pointerEvents: active ? "auto" : "none",
    transition: `opacity ${fadeMs}ms ease`,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  });

  // Overlay (AboutPanel-like container) fades in for captcha/sending/sent
  const overlayActive = stage !== "form";
  const overlayStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    opacity: overlayActive ? 1 : 0,
    pointerEvents: overlayActive ? "auto" : "none",
    transition: `opacity ${fadeMs}ms ease`,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  };

  const innerLayerStyle = (active: boolean): CSSProperties => ({
    position: "absolute",
    inset: 0,
    opacity: active ? 1 : 0,
    pointerEvents: active ? "auto" : "none",
    transition: `opacity ${fadeMs}ms ease`,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  });

  const formDisabled = stage !== "form";

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* ──────────────────────────────────────────────
          FORM LAYER (UNCHANGED VISUALLY)
          ────────────────────────────────────────────── */}
      <div style={layerStyle(stage === "form")}>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            gap: 18,
            minHeight: 0,
          }}
        >
          {/* Row 1: Name + Email */}
          <div style={{ display: "flex", flexDirection: "row", gap: 18, width: "100%" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Name</label>
              <input
                className="lf-contact-input"
                type="text"
                placeholder="Your name"
                value={name}
                disabled={formDisabled}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => setFocus((prev) => ({ ...prev, name: true }))}
                onBlur={() => setFocus((prev) => ({ ...prev, name: false }))}
                style={buildInputStyle("name")}
              />
            </div>

            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>E-mail</label>
              <input
                className="lf-contact-input"
                type="email"
                placeholder="name@email.com"
                value={email}
                disabled={formDisabled}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocus((prev) => ({ ...prev, email: true }))}
                onBlur={() => setFocus((prev) => ({ ...prev, email: false }))}
                style={buildInputStyle("email")}
              />
            </div>
          </div>

          {/* Row 2: Message */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <textarea
              className="lf-contact-input"
              placeholder="Write your message here..."
              value={message}
              disabled={formDisabled}
              onChange={(e) => setMessage(e.target.value)}
              onFocus={() => setFocus((prev) => ({ ...prev, message: true }))}
              onBlur={() => setFocus((prev) => ({ ...prev, message: false }))}
              style={{
                ...buildInputStyle("message"),
                height: "100%",
                minHeight: 90,
                resize: "none",
                lineHeight: 1.4,
              }}
            />

            <div
              style={{
                marginTop: 4,
                color: "#FF0031",
                fontFamily: theme.fonts.body,
                fontSize: 12,
                fontWeight: 400,
                opacity: showRequiredHint ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
            >
              *Required field
            </div>
          </div>

          {/* Footer: button + errors */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 6,
              marginBottom: 6,
            }}
          >
            {(serverError || captchaError) && (
              <div
                style={{
                  alignSelf: "flex-start",
                  color: "#FF0031",
                  fontFamily: theme.fonts.body,
                  fontSize: 12,
                  lineHeight: "normal",
                }}
              >
                {serverError ?? captchaError}
              </div>
            )}

            <button
              type="button"
              disabled={isSending || isVerifying}
              onClick={handleSubmit}
              onMouseEnter={() => {
                if (!isSending && !isVerifying) setHovered(true);
              }}
              onMouseLeave={() => {
                setHovered(false);
                setPressed(false);
              }}
              onMouseDown={(e) => {
                if (isSending || isVerifying) return;
                e.preventDefault();
                setPressed(true);
              }}
              onMouseUp={(e) => {
                if (isSending || isVerifying) return;
                e.preventDefault();
                setPressed(false);
              }}
              style={{
                minWidth: 300,
                height: 40,
                padding: "8px 14px",
                background,
                border,
                color,
                boxShadow,
                fontFamily: theme.fonts.body,
                fontSize: 16,
                cursor,
                outline: "none",
                boxSizing: "border-box",
                opacity: isSending || isVerifying ? 0.7 : 1,
              }}
            >
              {sendLabel}
            </button>
          </div>
        </div>
      </div>

      {/* ──────────────────────────────────────────────
          CAPTCHA + SENDING + SENT OVERLAY (AboutPanel-like container)
          ────────────────────────────────────────────── */}
      <div style={overlayStyle}>
        <div
          style={{
            background: theme.colors.DarkGray,
            boxShadow:
              "-1px -1px 0 0 #ffffff inset, 1px 1px 0 0 #808080 inset, -2px -2px 0 0 #c1c1c1 inset, 2px 2px 0 0 #000000 inset",
            padding: 16,
            flex: 1,
            alignSelf: "stretch",
            boxSizing: "border-box",
            overflow: "hidden",
            display: "flex",
            minHeight: 0,
          }}
        >
          {/* inner area */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
              background: theme.colors.Black,
              // optional subtle inset border like classic panels:
              boxShadow: "1px 1px 0 0 #000 inset, -1px -1px 0 0 #C1C1C1 inset",
            }}
          >
            {/* GAME (captcha + sending) */}
            <div style={innerLayerStyle(stage === "captcha" || stage === "sending")}>
              <div style={{ flex: 1, minHeight: 0 }}>
                <HumanCaptchaGame
                  orientation={orientation}
                  difficulty="normal"
                  mode={stage === "sending" ? "loading" : "play"}
                  playerImgSrc="/images/captcha/player.png"
                  goalImgSrc="/images/captcha/objective.png"
                  goalLoadingImgSrc="/images/captcha/objective.png"
                  showHint={stage === "captcha"}
                  onCollision={() => {
                    // optional: SFX/flash
                  }}
                  onSolved={() => {
                    setCaptchaSolved(true);
                    window.setTimeout(() => {
                      finalizeCaptchaAndSend();
                    }, 350);
                  }}
                />
              </div>

              {/* Verified beat */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 10,
                  display: "flex",
                  justifyContent: "center",
                  pointerEvents: "none",
                  opacity: captchaSolved ? 1 : 0,
                  transition: "opacity 160ms ease",
                }}
              >
                <div
                  style={{
                    padding: "6px 10px",
                    border: `1px solid ${theme.colors.Green}`,
                    background: theme.colors.Black,
                    color: theme.colors.Green,
                    fontFamily: theme.fonts.body,
                    fontSize: 12,
                  }}
                >
                  Verified.
                </div>
              </div>
            </div>

            {/* SENT SCREEN (embedded here) */}
            <div style={innerLayerStyle(stage === "sent")}>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 18,
                  padding: 14,
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    width: 132.129,
                    height: 128,
                    aspectRatio: "132.13 / 128.00",
                  }}
                >
                  {/* Provided SVG (embedded) */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="133" height="128" viewBox="0 0 133 128" fill="none">
                    <path d="M0 24.7742V82.5804H4.12903V28.9032H16.5161V24.7742H0Z" fill="#808080"/>
                    <path d="M128 24.7742H53.6774V28.9032H123.871V94.9675H115.613V99.0965H128V24.7742Z" fill="#808080"/>
                    <path d="M45.4193 94.9675H82.5806V99.0965H45.4193V94.9675Z" fill="#808080"/>
                    <path d="M53.6774 0H74.3226V4.12902H53.6774V0Z" fill="#C0C0C0"/>
                    <path d="M53.6774 4.12902V8.25804H45.4193V4.12902H53.6774Z" fill="#C0C0C0"/>
                    <path d="M74.3226 4.12902H78.4516V8.25804H74.3226V4.12902Z" fill="#C0C0C0"/>
                    <path d="M4.12903 28.9031V82.5804H8.25806V33.0321H16.5161V28.9031H4.12903Z" fill="#C0C0C0"/>
                    <path d="M123.871 28.9031H49.5484V33.0321H123.871V28.9031Z" fill="#C0C0C0"/>
                    <path d="M132.129 24.7742H128V99.0965H115.613V103.225H132.129V24.7742Z" fill="black"/>
                    <path d="M45.4194 99.0965H78.4517V103.225H45.4194V99.0965Z" fill="black"/>
                    <path d="M94.9678 41.29H90.8388V45.4191H94.9678V41.29Z" fill="#FFFF00"/>
                    <path d="M86.7097 49.5481H82.5807V53.6771H86.7097V57.8061H90.8388V53.6771H86.7097V49.5481Z" fill="#FFFF00"/>
                    <path d="M74.3226 57.8061H78.4517V61.9351H74.3226V57.8061Z" fill="#FFFF00"/>
                    <path d="M70.1936 66.0641H66.0646V70.1932H70.1936V66.0641Z" fill="#FFFF00"/>
                    <path d="M86.7097 74.3222H90.8388V78.4512H86.7097V74.3222Z" fill="#FFFF00"/>
                    <path d="M74.3226 82.5802H78.4517V86.7092H74.3226V82.5802Z" fill="#FFFF00"/>
                    <path d="M45.4194 86.7092H49.5485V90.8383H45.4194V86.7092Z" fill="#FFFF00"/>
                    <path d="M57.8065 90.8383H61.9356V94.9673H57.8065V90.8383Z" fill="#FFFF00"/>
                    <path d="M70.1936 86.7092H66.0646V90.8383H70.1936V86.7092Z" fill="#FFFF00"/>
                    <path d="M78.4517 90.8383H74.3226V94.9673H78.4517V90.8383Z" fill="#FFFF00"/>
                    <path d="M82.5807 90.8383H86.7097V94.9673H82.5807V90.8383Z" fill="#FFFF00"/>
                    <path d="M86.7097 90.8383V86.7092H90.8388V90.8383H86.7097Z" fill="#FFFF00"/>
                    <path d="M99.0968 45.4191H103.226V49.5481H99.0968V45.4191Z" fill="#FFFF00"/>
                    <path d="M103.226 53.6771H99.0968V57.8061H94.9678V61.9351H99.0968V57.8061H103.226V53.6771Z" fill="#FFFF00"/>
                    <path d="M111.484 66.0641H107.355V70.1932H111.484V66.0641Z" fill="#FFFF00"/>
                    <path d="M119.742 66.0641H123.871V70.1932H119.742V66.0641Z" fill="#FFFF00"/>
                    <path d="M119.742 74.3222H115.613V70.1932H119.742V74.3222Z" fill="#FFFF00"/>
                    <path d="M119.742 78.4512V74.3222H123.871V78.4512H119.742Z" fill="#FFFF00"/>
                    <path d="M119.742 82.5802H115.613V78.4512H119.742V82.5802Z" fill="#FFFF00"/>
                    <path d="M119.742 86.7092V82.5802H123.871V86.7092H119.742Z" fill="#FFFF00"/>
                    <path d="M119.742 90.8383H115.613V86.7092H119.742V90.8383Z" fill="#FFFF00"/>
                    <path d="M119.742 90.8383H123.871V94.9673H119.742V90.8383Z" fill="#FFFF00"/>
                    <path d="M115.613 53.6771H111.484V57.8061H115.613V61.9351H119.742V57.8061H123.871V53.6771H119.742V57.8061H115.613V53.6771Z" fill="#FFFF00"/>
                    <path d="M119.742 45.4191H123.871V49.5481H119.742V45.4191Z" fill="#FFFF00"/>
                    <path d="M111.484 74.3222H107.355V78.4512H111.484V74.3222Z" fill="#FFFF00"/>
                    <path d="M103.226 37.1614V53.6774H107.355V49.5484H111.484V45.4194H115.613V41.2904H119.742V37.1614H103.226Z" fill="#FF0000"/>
                    <path d="M119.742 41.29H115.613V45.4191H111.484V49.5481H107.355V53.6771H119.742V41.29Z" fill="#800000"/>
                    <path d="M53.6775 4.12891H74.3227V8.25792H66.0646V12.3869H61.9356V16.516H57.8065V12.3869H53.6775V16.516H49.5485V12.3869H45.4194V8.25792H53.6775V4.12891Z" fill="#00FFFF"/>
                    <path d="M45.4194 16.516H41.2904V12.3869H45.4194V16.516Z" fill="#00FFFF"/>
                    <path d="M45.4194 16.516V20.645H49.5485V16.516H45.4194Z" fill="#00FFFF"/>
                    <path d="M53.6775 16.516H57.8065V20.645H53.6775V16.516Z" fill="#00FFFF"/>
                    <path d="M90.8388 82.5802H111.484V103.225H107.355V99.0963H103.226V103.225H99.0968V99.0963H94.9678V103.225H90.8388V99.0963H86.7097V94.9673H90.8388V90.8383H94.9678V86.7093H90.8388V82.5802Z" fill="#00FFFF"/>
                    <path d="M86.7097 103.225H82.5807V99.0963H86.7097V103.225Z" fill="#00FFFF"/>
                    <path d="M86.7097 103.225V107.354H90.8388V103.225H86.7097Z" fill="#00FFFF"/>
                    <path d="M94.9678 103.225H99.0968V107.354H94.9678V103.225Z" fill="#00FFFF"/>
                    <path d="M74.3227 8.25806H78.4517V12.3871H74.3227V8.25806Z" fill="#0000FF"/>
                    <path d="M74.3227 12.3871V16.5161H66.0646V12.3871H74.3227Z" fill="#0000FF"/>
                    <path d="M78.4517 12.3871H82.5807V16.5161H78.4517V12.3871Z" fill="#0000FF"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M45.4194 24.7741H41.2904V28.9031H37.1614V24.7741H33.0324V20.6451H37.1614V16.5161H41.2904V12.3871H37.1614V16.5161H33.0324V20.6451H28.9033V24.7741H33.0324V28.9031H24.7743V24.7741H20.6453V16.5161H16.5162V45.4192H20.6453V37.1612H24.7743V41.2902H28.9033V45.4192H41.2904V41.2902H37.1614V37.1612H41.2904V28.9031H45.4194V24.7741ZM33.0324 28.9031V33.0322H37.1614V28.9031H33.0324Z" fill="#0000FF"/>
                    <path d="M49.5485 103.225H45.4194V111.483H49.5485V103.225Z" fill="#0000FF"/>
                    <path d="M49.5485 115.613H53.6775V119.742H49.5485V115.613Z" fill="#0000FF"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M74.3227 107.354H70.1936V111.483H61.9356V115.613H66.0646V119.742H57.8065V123.871H78.4517V119.742H82.5807V123.871H86.7097V119.742H90.8388V115.613H86.7097V119.742H82.5807V115.613H78.4517V111.483H74.3227V107.354ZM74.3227 115.613H78.4517V119.742H74.3227V115.613ZM74.3227 115.613V111.483H70.1936V115.613H74.3227Z" fill="#0000FF"/>
                    <path d="M78.4517 103.225V107.354H74.3227V103.225H78.4517Z" fill="#0000FF"/>
                    <path d="M82.5807 99.0964H78.4517V103.225H82.5807V99.0964Z" fill="#0000FF"/>
                    <path d="M86.7097 94.9674V99.0964H82.5807V94.9674H86.7097Z" fill="#0000FF"/>
                    <path d="M90.8388 90.8384H86.7097V94.9674H90.8388V90.8384Z" fill="#0000FF"/>
                    <path d="M90.8388 86.7094H94.9678V90.8384H90.8388V86.7094Z" fill="#0000FF"/>
                    <path d="M90.8388 86.7094V82.5804H111.484V78.4514H82.5807V82.5804H86.7097V86.7094H90.8388Z" fill="#0000FF"/>
                    <path d="M74.3227 8.25806H66.0646V12.3871H61.9356V16.5161H57.8065V20.6451H53.6775V24.7741H49.5485V28.9031H45.4194V33.0322H41.2904V37.1612H37.1614V41.2902H41.2904V45.4192H28.9033V41.2902H24.7743V37.1612H20.6453V45.4192H16.5162V49.5482H49.5485V45.4192H45.4194V41.2902H41.2904V37.1612H45.4194V33.0322H49.5485V28.9031H53.6775V24.7741H57.8065V20.6451H61.9356V16.5161H66.0646V12.3871H74.3227V8.25806Z" fill="#000080"/>
                    <path d="M20.6453 20.6451H24.7743V24.7741H20.6453V20.6451Z" fill="#000080"/>
                    <path d="M24.7743 24.7741H28.9033V28.9031H24.7743V24.7741Z" fill="#000080"/>
                    <path d="M107.355 103.225H111.484V78.4514H115.613V111.484H111.484V107.354H107.355V103.225Z" fill="#000080"/>
                    <path d="M103.226 103.225V99.0965H107.355V103.225H103.226Z" fill="#000080"/>
                    <path d="M103.226 103.225V107.354H99.0968V103.225H103.226Z" fill="#000080"/>
                    <path d="M53.6775 107.354H57.8065V111.484H53.6775V107.354Z" fill="#000080"/>
                    <path d="M53.6775 107.354H49.5485V103.225H53.6775V107.354Z" fill="#000080"/>
                    <path d="M53.6775 115.613H49.5485V111.484H53.6775V115.613Z" fill="#000080"/>
                    <path d="M57.8065 123.871H53.6775V115.613H66.0646V119.742H57.8065V123.871Z" fill="#000080"/>
                    <path d="M78.4517 123.871V128H57.8065V123.871H78.4517Z" fill="#000080"/>
                    <path d="M78.4517 123.871V119.742H82.5807V123.871H78.4517Z" fill="#000080"/>
                    <path d="M49.5485 61.9353H115.613V66.0643H49.5485V61.9353Z" fill="#000080"/>
                    <path d="M115.613 70.1933H49.5485V74.3223H115.613V70.1933Z" fill="#000080"/>
                    <path d="M49.5485 78.4514H82.5807V82.5804H49.5485V78.4514Z" fill="#000080"/>
                    <path d="M74.3227 16.5161H78.4517V20.6451H74.3227V16.5161Z" fill="#000080"/>
                    <path d="M78.4517 20.6451H82.5807V16.5161H86.7097V24.7741H78.4517V20.6451Z" fill="#000080"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M8.25806 33.0322H16.5161V49.5483H49.5484V45.4193H45.4193V41.2903H41.2903V37.1612H45.4193V33.0322H123.871V45.4193H119.742V37.1612H103.226V45.4193H99.0967V49.5483H103.226V53.6773H99.0967V57.8063H94.9677V61.9354H78.4516V57.8063H74.3225V61.9354H49.5484V66.0644H66.0645V70.1934H49.5484V74.3224H86.7096V78.4514H49.5484V82.5804H74.3225V86.7095H78.4516V82.5804H86.7096V90.8385H82.5806V94.9675H78.4516V90.8385H74.3225V94.9675H61.9355V90.8385H57.8064V94.9675H45.4193V90.8385H49.5484V86.7095H45.4193V82.5804H8.25806V33.0322ZM66.0645 86.7095V90.8385H70.1935V86.7095H66.0645ZM86.7096 57.8063H90.8387V53.6773H86.7096V49.5483H82.5806V53.6773H86.7096V57.8063ZM90.8387 41.2903H94.9677V45.4193H90.8387V41.2903Z" fill="white"/>
                    <path d="M115.613 61.9354H99.0967V57.8063H103.226V53.6773H111.484V57.8063H115.613V61.9354Z" fill="white"/>
                    <path d="M119.742 57.8063V61.9354H115.613V66.0644H111.484V70.1934H119.742V74.3224H111.484V78.4514H119.742V82.5804H115.613V86.7095H119.742V90.8385H115.613V94.9675H119.742V90.8385H123.871V86.7095H119.742V82.5804H123.871V78.4514H119.742V74.3224H123.871V70.1934H119.742V66.0644H123.871V57.8063H119.742Z" fill="white"/>
                    <path d="M119.742 53.6773V57.8063H115.613V53.6773H119.742Z" fill="white"/>
                    <path d="M119.742 53.6773V49.5483H123.871V53.6773H119.742Z" fill="white"/>
                    <path d="M107.355 66.0644H70.1935V70.1934H107.355V66.0644Z" fill="white"/>
                    <path d="M107.355 78.4514V74.3224H90.8387V78.4514H107.355Z" fill="white"/>
                    <rect x="4.12903" y="86.7092" width="37.1613" height="37.1612" fill="white"/>
                    <path d="M41.29 82.5803V86.7092H4.12891V123.87H0V82.5803H41.29Z" fill="#808080"/>
                    <path d="M45.4199 127.999H0V123.87H41.29V82.5803H45.4199V127.999Z" fill="black"/>
                    <path d="M37.1613 90.8384H33.0322V94.9674H28.9032V99.0964H24.7742V103.225H20.6451V107.354H16.5161V103.225H12.3871V99.0964H8.25806V111.483H12.3871V115.612H16.5161V119.742H20.6451V115.612H24.7742V111.483H28.9032V107.354H33.0322V103.225H37.1613V90.8384Z" fill="black"/>
                  </svg>
                </div>

                <div
                  style={{
                    color: theme.colors.White,
                    textAlign: "center",
                    fontFamily: theme.fonts.body,
                    fontSize: 32,
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "normal",
                    letterSpacing: 0,
                  }}
                >
                  Message sent successfully!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
